import { ChatBubbleLeftRightIcon, EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/solid";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger
} from "@moe/oss/ui/context-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@moe/oss/ui/dropdown-menu";
import { cn } from "@moe/oss/utils/utils";
import { ChatPreview as ChatPreviewI, DialogConfig } from "@moe/priv/types/types";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AvatarGroup } from "@web/components/AvatarGroup";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { chatPreviewQueryKey } from "@web/route-services/chat/use-chat-preview-query";
import { getChatTitle } from "@web/route-services/chat/utils";
import { useAppContext } from "@web/route-services/root/AppContext";
import { motion } from "motion/react";
import { useCallback } from "react";
import { toast } from "sonner";

interface ChatPreviewProps {
  chatID?: number;
  preview: ChatPreviewI;
  className?: string;
  [x: string]: any;
}
export function ChatPreview({ chatID, preview, className, ...rest }: ChatPreviewProps) {
  const { access, createDialog } = useAppContext();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const isActive = chatID === preview.chatID;
  const { isDesktopScreen } = useAppContext();

  const deleteChat = useCallback(async () => {
    const action = async () => {
      try {
        await access.chat.delete(preview.chatID);
        queryClient.invalidateQueries({ queryKey: chatPreviewQueryKey });
        toast.success("Chat deleted successfully");
        // If we're in the chat we just deleted, navigate to base chat route
        if (chatID === preview.chatID) {
          nav({ to: "/chat" });
        }
      } catch (e) {
        toast.error(`Failed to delete chat: ${e.message}`);
      }
    };
    const config: DialogConfig = {
      title: "Delete Chat",
      description: "Are you sure you want to delete this chat?",
      actionLabel: "Delete",
      onAction: action
    };
    createDialog(config);
  }, [access.chat, chatID, createDialog, nav, preview.chatID, queryClient]);

  const createChat = useCreateChat();

  const newChatHandler = useCallback(async () => {
    const chars = await access.character.getByChatID(preview.chatID);
    await createChat(chars[0]);
  }, [access.character, createChat, preview.chatID]);

  const avatars = preview.participants.map((p) => ({ url: p.avatar.toString() }));

  if (isDesktopScreen) {
    return (
      <ContextMenu>
        <ContextMenuTrigger>
          <Link
            draggable="false"
            to={`/chat?chatID=${preview.chatID}`}
            {...rest}
            className={cn(
              `group/preview group flex w-full cursor-pointer touch-manipulation items-center space-x-3 rounded-xl p-2.5 transition duration-200
              ease-out ${isActive ? "bg-accent" : ""}`,
              className
            )}
          >
            <AvatarGroup
              avatars={avatars}
              className="-space-x-5"
              avatarClassName="w-11 h-11"
              maxWidth="8rem"
              maxAvatars={3}
            />
            <div className="flex h-full w-full flex-col justify-center overflow-hidden">
              <div className="flex w-full">
                <h3 className="text-tx-primary line-clamp-1 text-ellipsis text-base font-semibold ">
                  {getChatPreviewTitle(preview)}
                </h3>
                <ActionDropdown
                  onDelete={deleteChat}
                  onNewChat={newChatHandler}
                  className="opacity-0 transition duration-300 ease-out group-hover/preview:opacity-100"
                />
              </div>
              <p className="text-tx-tertiary line-clamp-1 w-full text-ellipsis text-sm font-semibold">
                {getChatPreviewDescription(preview)}
              </p>
            </div>
          </Link>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-40 px-1 py-2">
          <ContextMenuItem onSelect={newChatHandler}>
            New Chat
            <ContextMenuShortcut>
              <ChatBubbleLeftRightIcon className="size-4" />
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={deleteChat}>
            Delete
            <ContextMenuShortcut>
              <TrashIcon className="size-4" />
            </ContextMenuShortcut>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  return (
    <motion.div
      whileTap={{
        scale: 0.97,
        backgroundColor: "var(--accent)"
      }}
      className={cn(
        `group flex w-full cursor-pointer items-center space-x-3 rounded-xl p-2.5 transition duration-200
    ease-out ${isActive ? "bg-accent" : ""}`,
        className
      )}
      onClick={() => {
        nav({ to: `/chat?chatID=${preview.chatID}` });
      }}
    >
      <Link
        to={`/chat?chatID=${preview.chatID}`}
        draggable="false"
        {...rest}
        className="flex flex-1 items-center space-x-3"
      >
        <AvatarGroup
          avatars={avatars}
          className="-space-x-5"
          avatarClassName="w-10 h-10"
          maxWidth="8rem"
          maxAvatars={3}
        />
        <div className="flex h-full w-full flex-col justify-center overflow-hidden">
          <div className="flex w-full">
            {/* Chat Title */}
            <h3 className="text-tx-primary line-clamp-1 text-ellipsis text-base font-semibold">
              {getChatPreviewTitle(preview)}
            </h3>
            <ActionDropdown onDelete={deleteChat} onNewChat={newChatHandler} />
          </div>
          {/* Chat Description */}
          <p className="text-tx-tertiary line-clamp-1 w-full text-ellipsis text-sm font-semibold">
            {getChatPreviewDescription(preview)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}

interface ActionDropdownProps {
  onDelete: () => void;
  onNewChat: () => void;
  className?: string;
}

const ActionDropdown = ({ onDelete, onNewChat, className }: ActionDropdownProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn("text-tx-primary ml-auto px-2 focus:outline-none", className)}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <EllipsisHorizontalIcon className="size-6" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={onNewChat} className="flex items-center">
          <ChatBubbleLeftRightIcon className="mr-2 size-4" />
          New Chat
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete} className="flex items-center">
          <TrashIcon className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function getChatPreviewTitle(preview: ChatPreviewI) {
  if (preview.participants.length <= 1) return preview.name;
  return getChatTitle(preview.participants.map((participant) => participant.name));
}

function getChatPreviewDescription(preview: ChatPreviewI) {
  if (preview.participants.length <= 1) return preview.text;
  return `${preview.participants.length} members`;
}
